import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Users, Calendar, Image, FileText, BarChart3,
  Settings, LogOut, Plus, TrendingUp, Clock, CheckCircle,
  Phone, Mail, MapPin,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PatientsTab from '../components/PatientsTab';
import AppointmentCalendar from '../components/AppointmentCalendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Trash2, UserPlus, Save, Image as ImageIcon } from 'lucide-react';
import ImageUploader from '../components/ImageUploader';
import MediaGallery from '../components/MediaGallery';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { formatDoctorName } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthConfig = () => {
  // Correctif B7 : le cookie httpOnly porte la session.
  return {
    withCredentials: true,
    headers: {},
  };
};

const ClinicDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'clinic_admin';

  useEffect(() => {
    if (authLoading) return;
    // Le compte principal (clinic_admin) et un compte secretariat
    // (clinic_staff) partagent ce meme tableau de bord ; les onglets
    // reserves au compte principal sont masques plus bas pour clinic_staff.
    if (!user || (user.role !== 'clinic_admin' && user.role !== 'clinic_staff')) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinic/profile`, getAuthConfig()),
        axios.get(`${API_URL}/api/clinic/stats`, getAuthConfig()),
      ]);
      setClinic(profileRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading || !clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#0596DE] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#64748B]">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0596DE] to-[#047CBA] flex items-center justify-center text-white">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-[#0F172A] text-sm md:text-base">{clinic.name}</h1>
              <p className="text-xs text-[#64748B]">{clinic.clinic_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            
              <a
              href={`/clinic/${clinic.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#0596DE] hover:underline hidden md:inline"
            >
              Voir page publique →
            </a>
            {isAdmin && clinic.is_pro && (
              <button onClick={() => { window.location.href="/clinic/pro"; }}
                className="text-xs bg-[#0596DE] text-white px-3 py-1.5 rounded-full font-medium hidden md:inline-flex items-center gap-1"
              >
                ⭐ Mode Pro
              </button>
            )}
            <a
              href={`/clinic/${clinic.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#0596DE] hover:underline hidden md:inline" style={{display:"none"}}
            >
              Voir page publique →
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] rounded-2xl p-6 mb-6 text-white">
          <h2 className="text-xl md:text-2xl font-bold mb-1">
            Bienvenue, {clinic.name}
          </h2>
          <p className="text-white/80 text-sm">
            Gérez vos médecins, partagez votre travail et suivez vos rendez-vous depuis cet espace.
          </p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="Médecins"
            value={stats?.total_doctors || 0}
            color="blue"
          />
          <StatCard
            icon={<Calendar className="w-5 h-5" />}
            label="Rendez-vous"
            value={stats?.total_appointments || 0}
            color="green"
          />
          <StatCard
            icon={<Image className="w-5 h-5" />}
            label="Portfolio"
            value={stats?.total_portfolio || 0}
            color="purple"
          />
          <StatCard
            icon={<FileText className="w-5 h-5" />}
            label="Articles"
            value={stats?.total_articles || 0}
            color="amber"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="bg-white rounded-2xl border border-[#E2E8F0]">
          <TabsList className="w-full rounded-t-2xl rounded-b-none p-0 h-auto bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <TabsTrigger value="overview" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
              <BarChart3 className="w-4 h-4 mr-2" /> Vue d'ensemble
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="doctors" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
                <Users className="w-4 h-4 mr-2" /> Médecins
              </TabsTrigger>
            )}
            <TabsTrigger value="patients" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
              👤 Patients
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
              <Calendar className="w-4 h-4 mr-2" /> Rendez-vous
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="portfolio" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
                <Image className="w-4 h-4 mr-2" /> Portfolio
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="articles" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
                <FileText className="w-4 h-4 mr-2" /> Articles
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="staff" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
                <UserPlus className="w-4 h-4 mr-2" /> Personnel
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="settings" className="flex-1 rounded-none data-[state=active]:bg-white py-3">
                <Settings className="w-4 h-4 mr-2" /> Paramètres
              </TabsTrigger>
            )}
          </TabsList>

          <div className="p-6">
            <TabsContent value="overview">
              <OverviewTab clinic={clinic} stats={stats} />
            </TabsContent>
            {isAdmin && (
              <TabsContent value="doctors">
                <DoctorsTab />
              </TabsContent>
            )}
            <TabsContent value="patients">
              <PatientsTab />
            </TabsContent>
            <TabsContent value="appointments">
              <AppointmentsTab />
            </TabsContent>
            {isAdmin && (
              <TabsContent value="portfolio">
                <PortfolioTab />
              </TabsContent>
            )}
            {isAdmin && (
              <TabsContent value="articles">
                <ArticlesTab />
              </TabsContent>
            )}
            {isAdmin && (
              <TabsContent value="staff">
                <StaffTab />
              </TabsContent>
            )}
            {isAdmin && (
              <TabsContent value="settings">
                <SettingsTab clinic={clinic} onUpdate={loadData} />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

// Stat card
const StatCard = ({ icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-[#64748B]">{label}</p>
          <p className="text-xl font-bold text-[#0F172A]">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Overview
const OverviewTab = ({ clinic, stats }) => (
  <div className="space-y-6">
    <div>
      <h3 className="font-semibold text-[#0F172A] mb-3">Informations de la clinique</h3>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <InfoRow icon={<MapPin />} label="Adresse" value={clinic.address} />
        <InfoRow icon={<Phone />} label="Téléphone" value={clinic.phone} />
        <InfoRow icon={<Mail />} label="Email" value={clinic.email} />
        <InfoRow icon={<Building2 />} label="Type" value={clinic.clinic_type} />
      </div>
    </div>

    {clinic.specialties?.length > 0 && (
      <div>
        <h3 className="font-semibold text-[#0F172A] mb-3">Spécialités proposées</h3>
        <div className="flex flex-wrap gap-2">
          {clinic.specialties.map((s, i) => (
            <span key={i} className="px-3 py-1 bg-[#E0F2FE] text-[#0596DE] rounded-full text-sm">
              {s}
            </span>
          ))}
        </div>
      </div>
    )}

    <div className="grid md:grid-cols-3 gap-4">
      <ActionCard
        icon={<Users className="w-6 h-6" />}
        title="Ajouter un médecin"
        description="Invitez un médecin à rejoindre votre clinique"
      />
      <ActionCard
        icon={<Image className="w-6 h-6" />}
        title="Publier une photo"
        description="Partagez vos réalisations avec vos patients"
      />
      <ActionCard
        icon={<FileText className="w-6 h-6" />}
        title="Écrire un article"
        description="Publiez des conseils et actualités"
      />
    </div>
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-[#F8FAFC] rounded-xl">
    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#0596DE]">
      {React.cloneElement(icon, { className: 'w-4 h-4' })}
    </div>
    <div className="flex-1">
      <p className="text-xs text-[#64748B]">{label}</p>
      <p className="font-medium text-[#0F172A]">{value || '-'}</p>
    </div>
  </div>
);

const ActionCard = ({ icon, title, description }) => (
  <div className="border border-[#E2E8F0] rounded-2xl p-4 hover:border-[#0596DE] hover:shadow-md transition cursor-pointer">
    <div className="w-12 h-12 rounded-full bg-[#E0F2FE] text-[#0596DE] flex items-center justify-center mb-3">
      {icon}
    </div>
    <h4 className="font-semibold text-[#0F172A] mb-1">{title}</h4>
    <p className="text-xs text-[#64748B]">{description}</p>
  </div>
);

const PlaceholderTab = ({ title, description }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-[#E0F2FE] text-[#0596DE] flex items-center justify-center mx-auto mb-4">
      <Clock className="w-8 h-8" />
    </div>
    <h3 className="font-semibold text-[#0F172A] mb-2">{title}</h3>
    <p className="text-sm text-[#64748B] mb-4">{description}</p>
    <p className="text-xs text-[#94A3B8]">Cette section sera bientôt disponible</p>
  </div>
);


// ────────── Onglet Médecins ──────────
const DoctorsTab = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [newDoc, setNewDoc] = useState({
    name: '', email: '', phone: '', specialty: '', fees: '', photo_url: '',
  });

  const loadDoctors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/clinic/doctors`, getAuthConfig());
      setDoctors(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoctors();
    axios.get(`${API_URL}/api/specialties`).then(r => setSpecialties(r.data)).catch(() => {});
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        ...newDoc,
        fees: newDoc.fees ? parseInt(newDoc.fees) : null,
      };
      const res = await axios.post(`${API_URL}/api/clinic/doctors`, payload, getAuthConfig());
      toast.success(`Dr. ${newDoc.name} ajouté. Mot de passe: ${res.data.temp_password}`);
      setDialogOpen(false);
      setNewDoc({ name: '', email: '', phone: '', specialty: '', fees: '', photo_url: '' });
      loadDoctors();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (doctor) => {
    if (!window.confirm(`Retirer Dr. ${doctor.name} de la clinique ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clinic/doctors/${doctor.doctor_id}`, getAuthConfig());
      toast.success('Médecin retiré');
      loadDoctors();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur, veuillez reessayer');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[#0F172A]">Mes médecins</h3>
          <p className="text-sm text-[#64748B]">Gérez l'équipe de votre clinique</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
          <UserPlus className="w-4 h-4 mr-1" /> Ajouter un médecin
        </Button>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#64748B]">Chargement...</p>
      ) : doctors.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E2E8F0] rounded-xl">
          <Users className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B] mb-4">Aucun médecin dans votre clinique</p>
          <Button onClick={() => setDialogOpen(true)} variant="outline" className="rounded-full">
            <UserPlus className="w-4 h-4 mr-1" /> Ajouter votre premier médecin
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {doctors.map((d) => (
            <div key={d.doctor_id} className="border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center text-[#0596DE] font-bold">
                {d.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#0F172A]">{formatDoctorName(d.name)}</h4>
                <p className="text-sm text-[#0596DE]">{d.specialty}</p>
                <p className="text-xs text-[#64748B] mt-1">{d.email}</p>
                <p className="text-xs text-[#64748B]">{d.phone}</p>
                {d.fees && <p className="text-xs text-[#0F172A] mt-1 font-medium">{d.fees} DA</p>}
              </div>
              <Button size="sm" variant="outline" onClick={() => handleRemove(d)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un médecin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label>Nom complet *</Label>
              <Input value={newDoc.name} onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })} required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newDoc.email} onChange={(e) => setNewDoc({ ...newDoc, email: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone *</Label>
                <Input value={newDoc.phone} onChange={(e) => setNewDoc({ ...newDoc, phone: e.target.value })} required />
              </div>
              <div>
                <Label>Spécialité *</Label>
                <select
                  value={newDoc.specialty}
                  onChange={(e) => setNewDoc({ ...newDoc, specialty: e.target.value })}
                  required
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
                >
                  <option value="">Sélectionner...</option>
                  {specialties.map((s) => (
                    <option key={s.code || s.name_fr} value={s.name_fr || s.code}>
                      {s.name_fr || s.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Tarif consultation (DA)</Label>
              <Input type="number" value={newDoc.fees} onChange={(e) => setNewDoc({ ...newDoc, fees: e.target.value })} placeholder="Ex: 2000" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                {actionLoading ? 'Ajout...' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};


// ────────── Onglet Personnel (compte principal uniquement) ──────────
const StaffTab = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', phone: '' });

  const loadStaff = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/clinic/staff`, getAuthConfig());
      setStaff(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/clinic/staff`, newStaff, getAuthConfig());
      toast.success(`${newStaff.name} ajouté(e). Mot de passe: ${res.data.temp_password}`);
      setDialogOpen(false);
      setNewStaff({ name: '', email: '', phone: '' });
      loadStaff();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Retirer ${member.name} de l'équipe ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clinic/staff/${member.staff_id}`, getAuthConfig());
      toast.success('Membre du personnel retiré');
      loadStaff();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur, veuillez reessayer');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[#0F172A]">Personnel</h3>
          <p className="text-sm text-[#64748B]">Comptes secrétariat / accueil — accès aux patients et rendez-vous uniquement</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
          <UserPlus className="w-4 h-4 mr-1" /> Ajouter un membre
        </Button>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#64748B]">Chargement...</p>
      ) : staff.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E2E8F0] rounded-xl">
          <Users className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B] mb-4">Aucun membre du personnel pour l'instant</p>
          <Button onClick={() => setDialogOpen(true)} variant="outline" className="rounded-full">
            <UserPlus className="w-4 h-4 mr-1" /> Ajouter votre premier membre
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {staff.map((s) => (
            <div key={s.staff_id} className="border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center text-[#0596DE] font-bold">
                {s.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#0F172A]">{s.name}</h4>
                <p className="text-xs text-[#64748B] mt-1">{s.email}</p>
                <p className="text-xs text-[#64748B]">{s.phone}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleRemove(s)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un membre du personnel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label>Nom complet *</Label>
              <Input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} required />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input value={newStaff.phone} onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })} required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                {actionLoading ? 'Ajout...' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ────────── Onglet Paramètres ──────────
const SettingsTab = ({ clinic, onUpdate }) => {
  const [form, setForm] = useState({
    name: clinic.name || '',
    phone: clinic.phone || '',
    address: clinic.address || '',
    daira: clinic.daira || '',
    commune: clinic.commune || '',
    description: clinic.description || '',
    website: clinic.website || '',
    logo_url: clinic.logo_url || '',
    cover_url: clinic.cover_url || '',
    specialties: (clinic.specialties || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [dairas, setDairas] = useState([]);

  useEffect(() => {
    if (clinic.wilaya_code) {
      axios.get(`${API_URL}/api/wilayas/${clinic.wilaya_code}/dairas`)
        .then(r => setDairas(r.data))
        .catch(() => setDairas([]));
    }
  }, [clinic.wilaya_code]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        specialties: form.specialties.split(',').map(s => s.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/clinic/profile`, payload, getAuthConfig());
      toast.success('Profil mis à jour');
      onUpdate();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
      <div>
        <h3 className="font-semibold text-[#0F172A] mb-3">Identité visuelle</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Label className="mb-2 block text-sm">Logo de la clinique</Label>
            <ImageUploader
              value={form.logo_url}
              onChange={(url) => setForm({ ...form, logo_url: url })}
              label="Upload logo"
              aspect="square"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="mb-2 block text-sm">Photo de couverture</Label>
            <ImageUploader
              value={form.cover_url}
              onChange={(url) => setForm({ ...form, cover_url: url })}
              label="Upload couverture"
              aspect="wide"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-[#0F172A] mb-3">Informations</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Nom de la clinique *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label>Téléphone *</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
          </div>
          <div className="md:col-span-2">
            <Label>Adresse complète *</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
          </div>
          <div>
            <Label>Daïra</Label>
            <select
              value={form.daira}
              onChange={(e) => setForm({ ...form, daira: e.target.value })}
              className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
            >
              <option value="">Sélectionner...</option>
              {dairas.map((d, i) => (
                <option key={i} value={typeof d === 'string' ? d : d.name_fr || d.name}>
                  {typeof d === 'string' ? d : d.name_fr || d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Commune</Label>
            <Input value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Site web</Label>
            <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
          </div>
          <div className="md:col-span-2">
            <Label>Spécialités (séparées par virgules)</Label>
            <Input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="Soins dentaires, Orthodontie, Implantologie" />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Présentez votre clinique, votre équipe, vos équipements..." />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-[#E2E8F0]">
        <Button type="submit" disabled={saving} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>
    </form>
  );
};


// ────────── Onglet Portfolio ──────────
const CATEGORIES = [
  { value: 'implant', label: 'Implant dentaire' },
  { value: 'orthodontie', label: 'Orthodontie' },
  { value: 'blanchiment', label: 'Blanchiment' },
  { value: 'prothese', label: 'Prothèse' },
  { value: 'couronne', label: 'Couronne / Bridge' },
  { value: 'chirurgie', label: 'Chirurgie' },
  { value: 'soins', label: 'Soins dentaires' },
  { value: 'autre', label: 'Autre' },
];

const PortfolioTab = () => {
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: '', doctor_id: '',
    before_url: '', after_url: '', media_urls: [], tags: '',
  });

  const loadItems = async () => {
    setLoading(true);
    try {
      const [itemsRes, doctorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinic/portfolio`, getAuthConfig()),
        axios.get(`${API_URL}/api/clinic/doctors`, getAuthConfig()),
      ]);
      setItems(itemsRes.data);
      setDoctors(doctorsRes.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const openCreate = () => {
    setEditingItem(null);
    setForm({ title: '', description: '', category: '', doctor_id: '', before_url: '', after_url: '', media_urls: [], tags: '' });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      description: item.description || '',
      category: item.category || '',
      doctor_id: item.doctor_id || '',
      before_url: item.before_url || '',
      after_url: item.after_url || '',
      media_urls: Array.isArray(item.media_urls) ? item.media_urls : [],
      tags: (item.tags || []).join(', '),
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error('Sélectionnez une catégorie'); return; }
    setActionLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        doctor_id: form.doctor_id || null,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/clinic/portfolio/${editingItem.item_id}`, payload, getAuthConfig());
        toast.success('Cas modifié');
      } else {
        await axios.post(`${API_URL}/api/clinic/portfolio`, payload, getAuthConfig());
        toast.success('Cas publié');
      }
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.title}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clinic/portfolio/${item.item_id}`, getAuthConfig());
      toast.success('Cas supprimé');
      loadItems();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[#0F172A]">Portfolio</h3>
          <p className="text-sm text-[#64748B]">Partagez vos cas cliniques avec photos avant/après</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un cas
        </Button>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#64748B]">Chargement...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E2E8F0] rounded-xl">
          <ImageIcon className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B] mb-4">Aucun cas dans votre portfolio</p>
          <Button onClick={openCreate} variant="outline" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Publier votre premier cas
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.item_id} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2">
                {item.before_url ? (
                  <div className="relative aspect-square bg-[#F8FAFC]">
                    <img src={item.before_url} alt="Avant" className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">Avant</span>
                  </div>
                ) : <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center text-[#94A3B8] text-xs">Pas de photo avant</div>}
                {item.after_url ? (
                  <div className="relative aspect-square bg-[#F8FAFC]">
                    <img src={item.after_url} alt="Après" className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#0596DE] text-white text-xs rounded-full">Après</span>
                  </div>
                ) : <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center text-[#94A3B8] text-xs">Pas de photo après</div>}
              </div>
              <div className="p-4">
                <span className="text-xs px-2 py-0.5 bg-[#E0F2FE] text-[#0596DE] rounded-full">{item.category}</span>
                <h4 className="font-semibold text-[#0F172A] mt-2">{item.title}</h4>
                {item.description && <p className="text-sm text-[#64748B] mt-1 line-clamp-2">{item.description}</p>}
                {item.doctor_name && <p className="text-xs text-[#94A3B8] mt-2">{formatDoctorName(item.doctor_name)}</p>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(item)} className="rounded-full flex-1">Modifier</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(item)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier le cas' : 'Publier un nouveau cas'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Titre du cas *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Pose d'implant unitaire" required />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie *</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  required
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="">Sélectionner...</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Médecin (optionnel)</Label>
                <select
                  value={form.doctor_id}
                  onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="">Aucun</option>
                  {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{formatDoctorName(d.name)}</option>)}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Photo "Avant"</Label>
                <ImageUploader
                  value={form.before_url}
                  onChange={(url) => setForm({ ...form, before_url: url })}
                  label="Upload photo avant"
                />
              </div>
              <div>
                <Label className="mb-2 block">Photo "Après"</Label>
                <ImageUploader
                  value={form.after_url}
                  onChange={(url) => setForm({ ...form, after_url: url })}
                  label="Upload photo après"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Photos / vidéos supplémentaires</Label>
              <MediaGallery
                value={form.media_urls}
                onChange={(arr) => setForm({ ...form, media_urls: arr })}
                maxItems={20}
                allowVideo={true}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Décrivez le cas, le traitement, les résultats..." />
            </div>

            <div>
              <Label>Tags (séparés par virgules)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="implant, esthetique, dent-anterieure" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                {actionLoading ? 'Enregistrement...' : (editingItem ? 'Modifier' : 'Publier')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ────────── Onglet Articles ──────────
const ArticlesTab = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', content: '', cover_url: '', tags: '', is_published: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/clinic/articles`, getAuthConfig());
      setArticles(res.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', content: '', cover_url: '', tags: '', is_published: true });
    setDialogOpen(true);
  };

  const openEdit = (art) => {
    setEditing(art);
    setForm({
      title: art.title || '',
      content: art.content || '',
      cover_url: art.cover_url || '',
      tags: (art.tags || []).join(', '),
      is_published: art.is_published !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/clinic/articles/${editing.article_id}`, payload, getAuthConfig());
        toast.success('Article modifié');
      } else {
        await axios.post(`${API_URL}/api/clinic/articles`, payload, getAuthConfig());
        toast.success('Article publié');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (art) => {
    if (!window.confirm(`Supprimer "${art.title}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clinic/articles/${art.article_id}`, getAuthConfig());
      toast.success('Article supprimé');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[#0F172A]">Articles & blog</h3>
          <p className="text-sm text-[#64748B]">Publiez des conseils et actualités pour vos patients</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Nouvel article
        </Button>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#64748B]">Chargement...</p>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E2E8F0] rounded-xl">
          <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B] mb-4">Aucun article publié</p>
          <Button onClick={openCreate} variant="outline" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Écrire votre premier article
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((a) => (
            <article key={a.article_id} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
              {a.cover_url && (
                <div className="aspect-video bg-[#F8FAFC]">
                  <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${a.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {a.is_published ? 'Publié' : 'Brouillon'}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <h4 className="font-semibold text-[#0F172A] line-clamp-2">{a.title}</h4>
                <p className="text-sm text-[#64748B] mt-1 line-clamp-3">{a.content}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} className="rounded-full flex-1">Modifier</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(a)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'article" : 'Nouvel article'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label className="mb-2 block">Image de couverture</Label>
              <ImageUploader
                value={form.cover_url}
                onChange={(url) => setForm({ ...form, cover_url: url })}
                label="Upload couverture"
                aspect="video"
              />
            </div>

            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Comment bien se brosser les dents" required />
            </div>

            <div>
              <Label>Contenu *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={10}
                placeholder="Rédigez votre article..."
                required
              />
            </div>

            <div>
              <Label>Tags (séparés par virgules)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hygiene, prevention, conseils" />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_published" className="cursor-pointer">Publier immédiatement (sinon brouillon)</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                {actionLoading ? 'Enregistrement...' : (editing ? 'Modifier' : 'Publier')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ────────── Onglet Rendez-vous ──────────
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

const AppointmentsTab = () => {
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDoctor, setFilterDoctor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedApt, setSelectedApt] = useState(null);
  const [transferDoctor, setTransferDoctor] = useState('');
  const [transferReason, setTransferReason] = useState('Médecin absent');
  const [transferDate, setTransferDate] = useState('');
  const [transferTime, setTransferTime] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [aptView, setAptView] = useState('list');
  const [calMonth, setCalMonth] = useState(new Date());
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleApt, setRescheduleApt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');

  const load = async (monthDate) => {
    setLoading(true);
    let aptUrl = API_URL + '/api/clinic/appointments';
    if (monthDate) {
      const y = monthDate.getFullYear();
      const mo = monthDate.getMonth();
      const pad = (n) => String(n).padStart(2, '0');
      const fd = y + '-' + pad(mo + 1) + '-01';
      const td = y + '-' + pad(mo + 1) + '-' + pad(new Date(y, mo + 1, 0).getDate());
      aptUrl = aptUrl + '?from_date=' + fd + '&to_date=' + td;
    }
    try {
      const [aptRes, docRes] = await Promise.all([
        axios.get(aptUrl, getAuthConfig()),
        axios.get(API_URL + '/api/clinic/doctors', getAuthConfig()),
      ]);
      setAppointments(aptRes.data);
      setDoctors(docRes.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aptView === 'calendar') {
      load(calMonth);
    } else {
      load();
    }
  }, [aptView, calMonth]);

  const openTransfer = (apt) => {
    setSelectedApt(apt);
    setTransferDoctor('');
    setTransferReason('Médecin absent');
    setTransferDate(apt.date || '');
    setTransferTime(apt.time || '');
    setTransferDialogOpen(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferDoctor) { toast.error('Sélectionnez un médecin'); return; }
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/clinic/appointments/${selectedApt.appointment_id}/transfer`,
        {
          doctor_id: transferDoctor,
          reason: transferReason,
          date: transferDate || null,
          time: transferTime || null,
        },
        getAuthConfig()
      );
      toast.success('Rendez-vous transféré, le patient a été notifié');
      setTransferDialogOpen(false);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const changeStatus = async (apt, newStatus) => {
    const labels = { confirmed: 'confirmer', completed: 'terminer', cancelled: 'annuler' };
    if (newStatus === 'cancelled' && !window.confirm('Annuler ce rendez-vous ?')) return;
    setActionLoading(true);
    try {
      await axios.put(
        API_URL + '/api/clinic/appointments/' + apt.appointment_id + '/status',
        { status: newStatus },
        getAuthConfig()
      );
      toast.success('Rendez-vous ' + labels[newStatus] + ' avec succes');
      if (aptView === 'calendar') { load(calMonth); } else { load(); }
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const openReschedule = (apt) => {
    setRescheduleApt(apt);
    setRescheduleDate(apt.date || '');
    setRescheduleTime(apt.time || '');
    setRescheduleOpen(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Choisissez une date et une heure');
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(
        API_URL + '/api/clinic/appointments/' + rescheduleApt.appointment_id + '/reschedule',
        { date: rescheduleDate, time: rescheduleTime },
        getAuthConfig()
      );
      toast.success('Rendez-vous reprogramme avec succes');
      setRescheduleOpen(false);
      if (aptView === 'calendar') { load(calMonth); } else { load(); }
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = appointments.filter(apt => {
    if (filterDoctor !== 'all' && apt.doctor_id !== filterDoctor) return false;
    if (filterStatus !== 'all' && apt.status !== filterStatus) return false;
    return true;
  });

  const counts = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="font-semibold text-[#0F172A]">Rendez-vous de la clinique</h3>
        <p className="text-sm text-[#64748B]">Vue globale des RDV de tous vos médecins</p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-[#F8FAFC] rounded-xl p-3 text-center">
          <p className="text-xs text-[#64748B]">Total</p>
          <p className="text-2xl font-bold text-[#0F172A]">{counts.total}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xs text-amber-700">En attente</p>
          <p className="text-2xl font-bold text-amber-700">{counts.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 text-center">
          <p className="text-xs text-blue-700">Confirmés</p>
          <p className="text-2xl font-bold text-blue-700">{counts.confirmed}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-xs text-green-700">Terminés</p>
          <p className="text-2xl font-bold text-green-700">{counts.completed}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <select
          value={filterDoctor}
          onChange={(e) => setFilterDoctor(e.target.value)}
          className="border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
        >
          <option value="all">Tous les médecins</option>
          {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{formatDoctorName(d.name)}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmés</option>
          <option value="completed">Terminés</option>
          <option value="cancelled">Annulés</option>
        </select>
      </div>

      {/* Bascule Liste / Calendrier */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setAptView('list')} className={'px-4 py-2 rounded-xl text-sm font-medium ' + (aptView === 'list' ? 'bg-[#0596DE] text-white' : 'bg-[#F8FAFC] text-[#64748B]')}>Liste</button>
        <button onClick={() => setAptView('calendar')} className={'px-4 py-2 rounded-xl text-sm font-medium ' + (aptView === 'calendar' ? 'bg-[#0596DE] text-white' : 'bg-[#F8FAFC] text-[#64748B]')}>Calendrier</button>
      </div>
      {/* Liste / Calendrier */}
      {loading ? (
        <p className="text-center py-12 text-[#64748B]">Chargement...</p>
      ) : aptView === 'calendar' ? (
        <AppointmentCalendar appointments={filtered} month={calMonth} setMonth={setCalMonth} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E2E8F0] rounded-xl">
          <Calendar className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B]">Aucun rendez-vous</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((apt) => (
            <div key={apt.appointment_id} className="border border-[#E2E8F0] rounded-xl p-4 hover:bg-[#F8FAFC] transition">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[apt.status] || 'bg-gray-100'}`}>
                      {STATUS_LABELS[apt.status] || apt.status}
                    </span>
                    <span className="text-sm font-semibold text-[#0F172A]">
                      {apt.date} à {apt.time}
                    </span>
                    {apt.transferred_from_name && (
                      <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        Transféré de {formatDoctorName(apt.transferred_from_name)}
                      </span>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-[#64748B] text-xs">Patient</p>
                      <p className="font-medium text-[#0F172A]">{apt.patient?.name || 'Inconnu'}</p>
                      {apt.patient?.phone && <p className="text-xs text-[#64748B]">{apt.patient.phone}</p>}
                    </div>
                    <div>
                      <p className="text-[#64748B] text-xs">Médecin</p>
                      <p className="font-medium text-[#0596DE]">{formatDoctorName(apt.doctor_name)}</p>
                    </div>
                  </div>
                  {apt.reason && (
                    <p className="text-sm text-[#64748B] mt-2 italic">Motif: {apt.reason}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 items-start">
                {apt.status === 'pending' && (
                  <Button size="sm" disabled={actionLoading} onClick={() => changeStatus(apt, 'confirmed')} className="rounded-full whitespace-nowrap bg-green-600 hover:bg-green-700 text-white">
                    Confirmer
                  </Button>
                )}
                {apt.status === 'confirmed' && (
                  <Button size="sm" disabled={actionLoading} onClick={() => changeStatus(apt, 'completed')} className="rounded-full whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white">
                    Terminer
                  </Button>
                )}
                {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                  <Button size="sm" variant="outline" disabled={actionLoading} onClick={() => openReschedule(apt)} className="rounded-full whitespace-nowrap">
                    Deplacer
                  </Button>
                )}
                {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                  <Button size="sm" variant="outline" disabled={actionLoading} onClick={() => changeStatus(apt, 'cancelled')} className="rounded-full whitespace-nowrap text-red-600 border-red-200 hover:bg-red-50">
                    Annuler
                  </Button>
                )}
                {apt.status !== 'completed' && apt.status !== 'cancelled' && doctors.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openTransfer(apt)}
                    className="rounded-full whitespace-nowrap"
                  >
                    Transférer
                  </Button>
                )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog transfert */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transférer le rendez-vous</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-xl p-3 text-sm">
              <p><strong>Patient:</strong> {selectedApt?.patient?.name}</p>
              <p><strong>Médecin actuel:</strong> {formatDoctorName(selectedApt?.doctor_name)}</p>
              <p><strong>Date:</strong> {selectedApt?.date} à {selectedApt?.time}</p>
            </div>
            <div>
              <Label>Nouveau médecin *</Label>
              <select
                value={transferDoctor}
                onChange={(e) => setTransferDoctor(e.target.value)}
                required
                className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
              >
                <option value="">Sélectionner...</option>
                {doctors
                  .filter(d => d.doctor_id !== selectedApt?.doctor_id)
                  .map(d => <option key={d.doctor_id} value={d.doctor_id}>{formatDoctorName(d.name)} ({d.specialty})</option>)
                }
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nouvelle date (optionnel)</Label>
                <Input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
              </div>
              <div>
                <Label>Nouvelle heure (optionnel)</Label>
                <Input type="time" value={transferTime} onChange={(e) => setTransferTime(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Raison du transfert</Label>
              <Input value={transferReason} onChange={(e) => setTransferReason(e.target.value)} placeholder="Ex: Médecin absent, urgence..." />
            </div>
            <p className="text-xs text-[#64748B] italic">
              Le patient sera notifié par email du changement de médecin.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                {actionLoading ? 'Transfert...' : 'Confirmer le transfert'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deplacer le rendez-vous</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F8FAFC] rounded-xl p-3 text-sm">
              <p><strong>Patient:</strong> {rescheduleApt?.patient?.name}</p>
              <p><strong>Medecin:</strong> {formatDoctorName(rescheduleApt?.doctor_name)}</p>
              <p><strong>Creneau actuel:</strong> {rescheduleApt?.date} a {rescheduleApt?.time}</p>
            </div>
            <div>
              <Label>Nouvelle date</Label>
              <Input type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} />
            </div>
            <div>
              <Label>Nouvelle heure</Label>
              <Input type="time" value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)} disabled={actionLoading}>Annuler</Button>
            <Button onClick={handleReschedule} disabled={actionLoading}>
              {actionLoading ? 'Enregistrement...' : 'Confirmer le deplacement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClinicDashboard;
